import React, { useState, useContext, useEffect, useRef } from 'react';
import {
  CContainer,
  CHeader,
  CHeaderBrand,
  CHeaderDivider,
  CHeaderNav,
  CDropdown,
  CDropdownMenu,
  CDropdownToggle,
  CDropdownItem,
  CCollapse,
  CButton,
  CNavbarNav,
  CNavItem,
  CNavLink,
} from '@coreui/react';
import { withFirebase } from 'src/views/firebase';
import { clearLocalCookies } from '../utils/useLocalStorage';
import { useNavigate } from 'react-router-dom';
import AppBreadcrumb from './AppBreadcrumb';
import AppHeaderDropdown from './header/AppHeaderDropdown';
import { ActiveLinkContext } from './ActiveLinkContext';
import logo from 'src/assets/images/milogo.png';
import homeIcon from '../assets/images/home.svg';
import financialsIcon from '../assets/images/financials.svg';
import healthIcon from '../assets/images/health.svg';
import mieIcon from '../assets/images/mie_sprite.png';
import mieRobotIcon from '../assets/images/mieros_circle.png';
import socialIcon from '../assets/images/socials.svg';
import cryptoIcon from '../assets/images/crypto.svg';
import robotIcon from '../assets/images/mieros_circle.png';
import { toast } from 'react-toastify';
import marketIcon from '../assets/images/marketplace.svg';
import metaverseIcon from '../assets/images/metaverse.svg';
import cartIcon from '../assets/cart.png';
import ScheduledTaskNotifier from './ScheduledTaskNotifier';
import '../scss/main.scss';
import { useCart } from '../context/CartContext';

const navItems = [
  { 
    label: 'MiE', 
    icon: mieIcon ,
    dropdown: true,
    items: [
      { label: 'Circadian Rhythm', link: '/', icon: '../assets/images/social.svg' },
      { label: 'Profile', link: 'profile', icon: '../assets/images/social.svg' },
      { label: 'Scheduler', link: 'scheduler', icon: '../assets/images/scheduler.svg' },
      { label: 'Notes', link: 'notes', icon: '../assets/images/scheduler.svg' },
    ],
  },
  {
    label: 'Health',
    icon: healthIcon,
    dropdown: true,
    items: [
      { label: 'Health Dashboard', link: 'health', icon: '../assets/images/health.svg' },
      { label: 'DNA Dashboard', link: 'dna', icon: '../assets/images/dna.svg' },
      { label: 'Mood Ring', link: 'moodRing', icon: '../assets/images/moodring.svg' },
      { label: 'CCD Converter', link: 'ccdconverter', icon: '../assets/images/ccdconverter.svg' },
    ],
  },
  {
    label: 'Wealth',
    icon: financialsIcon,
    dropdown: true,
    items: [
      { label: 'Expenses', link: 'expenses', icon: '../assets/images/social.svg' },
      { label: 'Market', link: 'crypto', icon: '../assets/images/home.svg' },
      { label: 'My Stuff', link: 'digiart', icon: '../assets/images/mystuff.svg' },
      { label: 'Creator Dashboard', link: 'creator', icon: '../assets/images/social.svg' },
      { label: 'MiE Blockchain', link: 'explorer', icon: '../assets/images/blockchain.svg' },
    ],
  },
  {
    label: 'Happiness',
    icon: socialIcon,
    dropdown: true,
    items: [
      { label: 'Metaverse', link: 'meta', icon: metaverseIcon },
      { label: 'Social Dashboard', link: 'social', icon: '../assets/images/social.svg' },
    ],
  },
  { label: 'Shop', link: 'market', icon: cryptoIcon },
  {
    label: 'Agents',
    icon: robotIcon,
    dropdown: true,
    items: [
      { label: 'My Agents', link: '/dashboard/agents' },
      { label: 'Create Agent', link: '/dashboard/agents/new' },
      { label: 'Living Profile', link: '/dashboard/profile/agent' },
    ],
  },
  {
    label: 'Devices',
    icon: homeIcon,
    dropdown: true,
    items: [
      { label: 'IoT Hub', link: '/dashboard/iot', icon: '../assets/images/dashboard.svg' },
      { label: 'PiBots', link: '/dashboard/iot/pibots', icon: '../assets/images/mybots.svg' },
    ],
  },
  { label: 'Cart', link: 'cart', icon: cartIcon },
];

const AppHeader = ({ authUser, firebase, fhirToken }) => {
  const navigate = useNavigate();
  const { activeLink, setActiveLink } = useContext(ActiveLinkContext);
  const [dropdownOpen, setDropdownOpen] = useState({});
  const linkRef = useRef(null);
  const { cartItems } = useCart();
  const cartItemsCount = cartItems.length;
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  const handleNavClick = (link) => {
    setDropdownOpen({});
    
    // console.log('handleNavClick called with link:', link);
    // console.log('Current location:', window.location.pathname);
    // console.log('Current activeLink:', activeLink);
    
    // Store the intended link
    linkRef.current = link;
    
    // Special handling for direct routes
    if (link === 'cart') {
      setActiveLink(link);
      navigate('/cart');
      return;
    }

    if (link === 'orders') {
      setActiveLink(link);
      navigate('/orders');
      return;
    }

    if (link === 'market') {
      setActiveLink(link);
      navigate('/marketplace');
      return;
    }

    if (link === 'creator') {
      setActiveLink(link);
      navigate('/dashboard/creator/home', {
        state: {
          activeLink: link,
          timestamp: Date.now()
        }
      });
      return;
    }

    // Direct dashboard routes (agents, profile)
    if (typeof link === 'string' && link.startsWith('/dashboard/')) {
      setActiveLink(link);
      navigate(link);
      return;
    }

    // If coming from marketplace
    if (window.location.pathname === '/marketplace') {
     // console.log('Navigating from marketplace to:', link);
      
      setActiveLink(link);
      navigate('/dashboard', {
        replace: true,
        state: {
          activeLink: link,
          timestamp: Date.now(),
          fromMarketplace: true
        }
      });
      return;
    }
    
    // For all other cases
    setActiveLink(link);
    navigate('/dashboard', {
      replace: true,
      state: {
        activeLink: link,
        timestamp: Date.now()
      }
    });
  };

  // Monitor location changes and sync activeLink
  useEffect(() => {
    const currentPath = window.location.pathname;
    const currentState = window.history.state;
    
    if (linkRef.current) {
      setActiveLink(linkRef.current);
    } else if (currentState?.activeLink) {
      setActiveLink(currentState.activeLink);
      linkRef.current = currentState.activeLink;
    }
    
    // console.log('Location changed:', {
    //   path: currentPath,
    //   state: currentState,
    //   activeLink: activeLink,
    //   intendedLink: linkRef.current
    // });
  }, [window.location.pathname, setActiveLink]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      // Preserve the last active link when component unmounts
      if (linkRef.current) {
        window.history.replaceState(
          { ...window.history.state, activeLink: linkRef.current },
          ''
        );
      }
    };
  }, []);

  const toggleDropdown = (index) => {
    setDropdownOpen((prevState) => {
      if (prevState[index]) {
        return {};
      }
      return {
        [index]: true
      };
    });
  };

  const renderNavItems = (items, isMobile = false) => {
    return items.map((item, index) => {
      if (item.label === 'Cart') {
        return (
          <CNavItem key={index}>
            <CNavLink
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('/cart')}
            >
              <div className="cart-icon-container">
                <img
                  src={item.icon}
                  style={{ width: '24px', height: '24px', marginRight: '8px' }}
                />
                {cartItemsCount > 0 && (
                  <span className="cart-badge">
                    {cartItemsCount > 99 ? '99+' : cartItemsCount}
                  </span>
                )}
                {item.label}
              </div>
            </CNavLink>
          </CNavItem>
        );
      }
      if (item.dropdown) {
        return (
          <CDropdown
            key={index}
            variant="nav-item"
            className={isMobile ? '' : 'me-2'}
            visible={dropdownOpen[index]}
            onClick={() => toggleDropdown(index)}
          >
            <CDropdownToggle caret={false}>
              <img
                src={item.icon}
                alt={item.label}
                style={{ width: '24px', height: '24px', marginRight: '8px' }}
              />
              {item.label}
            </CDropdownToggle>
            <CDropdownMenu>
              {item.items.map((subItem, subIndex) => (
                <CDropdownItem
                  key={subIndex}
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDropdownOpen({});
                    handleNavClick(subItem.link);
                  }}
                >
                  {subItem.label}
                </CDropdownItem>
              ))}
            </CDropdownMenu>
          </CDropdown>
        );
      } else {
        return (
          <CNavItem key={index}>
            <CNavLink
              style={{ cursor: 'pointer' }}
              onClick={() => handleNavClick(item.link)}
            >
              <img
                src={item.icon}
                style={{ width: '24px', height: '24px', marginRight: '8px' }}
              />
              {item.label}
            </CNavLink>
          </CNavItem>
        );
      }
    });
  };

  const renderMobileIcons = (items) => {
    return (
      <div
        className="mobile-icons"
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '15px',
          flexWrap: 'wrap',
          position: 'relative',
        }}
      >
        {items.map((item, index) => (
          <div key={index} style={{ position: 'relative' }}>
            <button
              onClick={() =>
                item.dropdown ? toggleDropdown(index) : handleNavClick(item.link)
              }
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <img
                src={item.icon}
                style={{ width: '25px', height: '25px' }}
              />
            </button>
            {item.dropdown && dropdownOpen[index] && (
              <div
                className="header-nav-dropdown-mobile"
                style={{
                  position: 'fixed',
                  top: '60px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'rgba(30, 41, 59, 0.98)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  borderRadius: '12px',
                  padding: '8px 0',
                  zIndex: 1060,
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
                  maxHeight: '80vh',
                  overflowY: 'auto',
                  width: '200px',
                  maxWidth: '90vw',
                }}
              >
                {item.items.map((subItem, subIndex) => (
                  <div
                    key={subIndex}
                    className="header-nav-dropdown-mobile-item"
                    style={{
                      padding: '12px 16px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      display: 'flex',
                      alignItems: 'center',
                      borderBottom: subIndex !== item.items.length - 1 ? '1px solid rgba(255, 255, 255, 0.08)' : 'none',
                      color: '#F8FAFC',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNavClick(subItem.link);
                      setDropdownOpen({});
                    }}
                  >
                    {subItem.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <React.Fragment>
      <CHeader
        className={`app-header ${sidebarCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded'}`}
        position="sticky"
      >
        <CContainer fluid>
          <div className="header-nav">
            <div className="d-md-none d-md-flex me-auto">{renderMobileIcons(navItems)}</div>

            <CHeaderNav className="d-none d-md-flex me-auto">
              {renderNavItems(navItems)}
            </CHeaderNav>

            <CHeaderNav className="header-dropdown">
              <ScheduledTaskNotifier authUser={authUser} fhirToken={fhirToken} compact />
              <AppHeaderDropdown 
                authUser={authUser} 
              />
            </CHeaderNav>
          </div>
        </CContainer>
      </CHeader>
    </React.Fragment>
  );
};

export default withFirebase(AppHeader);
